import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  LinearProgress,
  CircularProgress,
  Stack,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Collapse,
  Paper,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ImageIcon from "@mui/icons-material/Image";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import html2canvas from "html2canvas";

import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import { ContactsThankYouMessage } from "./ContactsThankYouMessage";

export interface ContactItem {
  id: string;
  name: string;
  phone: string;
  email: string;
  source: string;
}

interface BulkWhatsAppModalProps {
  open: boolean;
  onClose: () => void;
  contacts: ContactItem[];
  deceasedName?: string;
  streamLink?: string;
  onSent?: (sentContactIds: string[]) => void;
}

interface BatchRecipientResult {
  phone: string;
  name: string;
  status: "sent" | "failed";
  error?: string;
  skipped_duplicate_retry?: boolean;
}

interface BatchProgress {
  batch_id?: string;
  status: "queued" | "processing" | "completed" | "failed";
  total: number;
  processed: number;
  sent: number;
  failed: number;
  results: BatchRecipientResult[];
}

const isRealPhone = (value?: string | null): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "n/a" &&
    normalized !== "na" &&
    normalized !== "undefined"
  );
};

const compileMessage = (
  templateStr: string,
  name: string,
  deceasedName: string,
  streamLink: string,
): string => {
  return templateStr
    .replace(/\{name\}/gi, name || "Friend")
    .replace(/\{deceasedName\}/gi, deceasedName || "our beloved")
    .replace(/\{streamLink\}/gi, streamLink || "");
};

const initialsOf = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

const avatarPalette = [
  "#0f766e",
  "#7c3aed",
  "#b45309",
  "#0369a1",
  "#be123c",
  "#4d7c0f",
];

const avatarColorFor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

export const BulkWhatsAppModal: React.FC<BulkWhatsAppModalProps> = ({
  open,
  onClose,
  contacts,
  deceasedName = "",
  streamLink = "",
  onSent,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [customDeceasedName, setCustomDeceasedName] = useState(deceasedName);

  useEffect(() => {
    if (deceasedName) {
      setCustomDeceasedName(deceasedName);
    }
  }, [deceasedName]);

  const effectiveDeceasedName = (
    customDeceasedName ||
    deceasedName ||
    "our beloved"
  ).trim();

  const defaultTemplate =
    "Hi {name}, I'm Tawanda from Muzukuru Funeral Streaming.\n\nThank you for your Memory message for {deceasedName} 🙏 I've added it to the Memory Book, your words will mean a lot to the family.";

  const [messageTemplate] = useState(defaultTemplate);
  const [sendFormat, setSendFormat] = useState<"image" | "text">("image");
  const [modalStage, setModalStage] = useState<
    "compose" | "generating" | "monitoring" | "error"
  >("compose");
  const [statusStep, setStatusStep] = useState<string>("");
  const [statusSubtitle, setStatusSubtitle] = useState<string>("");
  const [showRecipientsList, setShowRecipientsList] = useState(false);
  const [skippedOpen, setSkippedOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Live Batch Tracking State
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [filterTab, setFilterTab] = useState<"all" | "sent" | "failed">("all");

  // Group valid vs invalid
  const { validContacts, invalidContacts } = useMemo(() => {
    const valid: ContactItem[] = [];
    const invalid: ContactItem[] = [];
    contacts.forEach((c) => {
      if (isRealPhone(c.phone)) {
        valid.push(c);
      } else {
        invalid.push(c);
      }
    });
    return { validContacts: valid, invalidContacts: invalid };
  }, [contacts]);

  // Keep preview index in bounds
  useEffect(() => {
    if (previewIndex >= validContacts.length && validContacts.length > 0) {
      setPreviewIndex(0);
    }
  }, [validContacts.length, previewIndex]);

  const previewContact = validContacts[previewIndex] || contacts[0];

  const previewMessage = useMemo(() => {
    if (!previewContact) return "";
    return compileMessage(
      messageTemplate,
      previewContact.name,
      effectiveDeceasedName,
      streamLink,
    );
  }, [messageTemplate, previewContact, effectiveDeceasedName, streamLink]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const generateCardBase64 = async (): Promise<string | null> => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        width: 1080,
        height: 1080,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      console.error("Failed to capture graphic card:", err);
      return null;
    }
  };

  const startPolling = (batchId: string, initialTotal: number) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await apiService.get<{
          ok: boolean;
          batch_id: string;
          status: "queued" | "processing" | "completed" | "failed";
          total: number;
          processed: number;
          sent: number;
          failed: number;
          results: BatchRecipientResult[];
        }>(`/api/whatsapp/batch-status/${batchId}/`);

        if (res.data.ok) {
          const total = res.data.total || initialTotal;
          const processed = res.data.processed || 0;
          const sent = res.data.sent || 0;
          const failed = res.data.failed || 0;
          const results = res.data.results || [];
          const status = res.data.status || "processing";

          setBatchProgress({
            batch_id: batchId,
            status,
            total,
            processed,
            sent,
            failed,
            results,
          });

          if (status === "completed" || status === "failed") {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            if (status === "completed") {
              const successfulPhones = new Set(
                results.filter((r) => r.status === "sent").map((r) => r.phone),
              );
              const sentIds = validContacts
                .filter((c) => successfulPhones.has(c.phone))
                .map((c) => c.id);
              if (onSent) {
                onSent(sentIds.length > 0 ? sentIds : validContacts.map((c) => c.id));
              }
            }
          }
        }
      } catch (err) {
        console.error("Error polling batch status:", err);
      }
    }, 1200);
  };

  const handleSendBulk = async () => {
    if (validContacts.length === 0) {
      showErrorToast("No selected contacts have valid phone numbers.");
      return;
    }

    setModalStage("generating");
    setBatchProgress(null);

    let imageB64: string | null = null;
    if (sendFormat === "image") {
      setStatusStep("Generating personalized graphic card...");
      setStatusSubtitle(
        `Rendering tribute visual for ${effectiveDeceasedName}...`,
      );
      imageB64 = await generateCardBase64();
      if (!imageB64) {
        showErrorToast("Couldn't generate graphic card. Please try again.");
        setModalStage("compose");
        return;
      }
    }

    setStatusStep("Sending messages...");
    setStatusSubtitle(
      `Preparing ${validContacts.length} personalized message(s)...`,
    );

    const recipientsPayload = validContacts.map((c) => ({
      name: c.name,
      phone: c.phone,
      message: compileMessage(
        messageTemplate,
        c.name,
        effectiveDeceasedName,
        streamLink,
      ),
      deceased_name: effectiveDeceasedName,
    }));

    try {
      const response = await apiService.post<{
        ok: boolean;
        batch_id?: string;
        sent?: number;
        failed?: number;
        queued?: boolean;
        total?: number;
        message?: string;
        results?: BatchRecipientResult[];
      }>("/api/whatsapp/send-bulk/", {
        recipients: recipientsPayload,
        provider: "meta",
        image_b64: imageB64 || undefined,
        template_name:
          sendFormat === "image" ? "bulk_thank_you_messages" : undefined,
        deceased_name: effectiveDeceasedName,
        template_lang: "en",
      });

      if (response.data.ok) {
        const batchId = response.data.batch_id;
        const isQueued = Boolean(response.data.queued);

        if (isQueued && batchId) {
          setActiveBatchId(batchId);
          setBatchProgress({
            batch_id: batchId,
            status: "processing",
            total: response.data.total || validContacts.length,
            processed: 0,
            sent: 0,
            failed: 0,
            results: [],
          });
          setModalStage("monitoring");
          startPolling(batchId, validContacts.length);
        } else {
          // Processed synchronously (e.g. 1-2 contacts)
          const resultsList = response.data.results || [];
          const sent = response.data.sent ?? resultsList.filter((r) => r.status === "sent").length;
          const failed = response.data.failed ?? resultsList.filter((r) => r.status === "failed").length;

          setBatchProgress({
            status: "completed",
            total: validContacts.length,
            processed: validContacts.length,
            sent,
            failed,
            results: resultsList,
          });
          setModalStage("monitoring");

          if (onSent) {
            const successfulPhones = new Set(
              resultsList.filter((r) => r.status === "sent").map((r) => r.phone),
            );
            const sentIds = validContacts
              .filter((c) => successfulPhones.has(c.phone))
              .map((c) => c.id);
            onSent(sentIds.length > 0 ? sentIds : validContacts.map((c) => c.id));
          }
        }
      } else {
        setModalStage("error");
        showErrorToast("Failed to initiate bulk WhatsApp send.");
      }
    } catch (err: unknown) {
      console.error("Bulk WhatsApp error:", err);
      setModalStage("error");
      showErrorToast(
        "An error occurred while sending bulk WhatsApp messages via Meta API.",
      );
    }
  };

  const handleFinish = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    onClose();
  };

  const handlePrevContact = () => {
    setPreviewIndex((prev) =>
      prev > 0 ? prev - 1 : Math.max(0, validContacts.length - 1),
    );
  };

  const handleNextContact = () => {
    setPreviewIndex((prev) =>
      prev < validContacts.length - 1 ? prev + 1 : 0,
    );
  };

  // Build merged recipient list with live status
  const mergedRecipients = useMemo(() => {
    const resultsMap = new Map<string, BatchRecipientResult>();
    (batchProgress?.results || []).forEach((r) => {
      // Index by raw or normalized phone
      resultsMap.set(r.phone.replace(/\D/g, ""), r);
    });

    return validContacts.map((c) => {
      const cleanPhone = c.phone.replace(/\D/g, "");
      const res = resultsMap.get(cleanPhone);
      return {
        id: c.id,
        name: c.name || "Unnamed",
        phone: c.phone,
        status: res ? res.status : "pending",
        error: res?.error,
      };
    });
  }, [validContacts, batchProgress?.results]);

  const filteredRecipients = useMemo(() => {
    if (filterTab === "sent") {
      return mergedRecipients.filter((r) => r.status === "sent");
    }
    if (filterTab === "failed") {
      return mergedRecipients.filter((r) => r.status === "failed");
    }
    return mergedRecipients;
  }, [mergedRecipients, filterTab]);

  const failedRecipients = useMemo(() => {
    return mergedRecipients.filter((r) => r.status === "failed");
  }, [mergedRecipients]);

  const handleCopyFailedNumbers = () => {
    if (failedRecipients.length === 0) return;
    const text = failedRecipients
      .map((r) => `${r.name}: ${r.phone} (${r.error || "Delivery failed"})`)
      .join("\n");

    navigator.clipboard.writeText(text);
    showSuccessToast(
      `Copied ${failedRecipients.length} failed contact(s) to clipboard.`,
    );
  };

  const isCompleted = batchProgress?.status === "completed";
  const progressPercent = Math.min(
    100,
    Math.round(
      ((batchProgress?.processed ?? 0) /
        Math.max(1, batchProgress?.total ?? validContacts.length)) *
        100,
    ),
  );

  return (
    <Dialog
      open={open}
      onClose={modalStage === "generating" ? undefined : handleFinish}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.16)",
          border: "1px solid #e2e8f0",
        },
      }}
    >
      {/* Hidden full-resolution DOM node for html2canvas rendering */}
      <Box
        sx={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: 1080,
          height: 1080,
          pointerEvents: "none",
          zIndex: -100,
          bgcolor: "#ffffff",
        }}
      >
        <Box ref={cardRef} sx={{ width: 1080, height: 1080 }}>
          <ContactsThankYouMessage deceasedName={effectiveDeceasedName} />
        </Box>
      </Box>

      {/* HEADER */}
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.25,
          bgcolor: "#ffffff",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "#25D366",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(37, 211, 102, 0.28)",
              }}
            >
              <WhatsAppIcon sx={{ color: "#ffffff", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a" }}>
                {modalStage === "monitoring"
                  ? isCompleted
                    ? "Delivery Report"
                    : "Delivering WhatsApp Messages"
                  : "Send Bulk WhatsApp Messages"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {modalStage === "monitoring"
                  ? isCompleted
                    ? `Processed all ${batchProgress?.total ?? validContacts.length} recipient(s)`
                    : `Sending ${batchProgress?.processed ?? 0} of ${batchProgress?.total ?? validContacts.length} in background...`
                  : "Send personalized thank-you messages to your contacts"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleFinish}
            disabled={modalStage === "generating"}
            size="small"
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#334155", bgcolor: "#f1f5f9" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* DIALOG CONTENT */}
      <DialogContent sx={{ p: 0, bgcolor: "#ffffff" }}>
        {/* STAGE 1: COMPOSE (SPLIT PANE) */}
        {modalStage === "compose" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              minHeight: 520,
            }}
          >
            {/* LEFT PANE: CONFIGURATION & AUDIENCE */}
            <Box
              sx={{
                flex: { xs: "1 1 auto", md: "0 0 52%" },
                p: 3,
                borderRight: { md: "1px solid #f1f5f9" },
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {/* Audience Summary Card */}
              <Box
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  bgcolor: "#f8fafc",
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b" }}>
                    Audience
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowRecipientsList((v) => !v)}
                    endIcon={
                      showRecipientsList ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#0f766e",
                      p: 0,
                    }}
                  >
                    {showRecipientsList ? "Hide List" : "View List"}
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a!important" }} />}
                    label={`${validContacts.length} Ready to Send`}
                    size="small"
                    sx={{
                      bgcolor: "#ecfdf5",
                      color: "#065f46",
                      fontWeight: 600,
                      border: "1px solid #a7f3d0",
                    }}
                  />
                  {invalidContacts.length > 0 && (
                    <Chip
                      icon={
                        <PhoneDisabledIcon
                          sx={{ fontSize: 15, color: "#b45309!important" }}
                        />
                      }
                      label={`${invalidContacts.length} Skipped (no phone)`}
                      size="small"
                      onClick={() => setSkippedOpen((v) => !v)}
                      sx={{
                        bgcolor: "#fffbeb",
                        color: "#92400e",
                        fontWeight: 600,
                        border: "1px solid #fde68a",
                        cursor: "pointer",
                      }}
                    />
                  )}
                </Stack>

                {/* Collapsible Full Recipients List */}
                <Collapse in={showRecipientsList}>
                  <Box
                    sx={{
                      mt: 1.5,
                      maxHeight: 180,
                      overflowY: "auto",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      bgcolor: "#ffffff",
                    }}
                  >
                    {validContacts.map((c, idx) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 0.8,
                          borderBottom:
                            idx < validContacts.length - 1
                              ? "1px solid #f1f5f9"
                              : "none",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: avatarColorFor(c.id || c.name),
                          }}
                        >
                          {initialsOf(c.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            noWrap
                            display="block"
                            sx={{ color: "#1e293b" }}
                          >
                            {c.name || "Unnamed"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b", fontSize: 11 }}
                          >
                            {c.phone}
                          </Typography>
                        </Box>
                        <CheckCircleIcon sx={{ color: "#25D366", fontSize: 16 }} />
                      </Box>
                    ))}
                  </Box>
                </Collapse>

                {/* Collapsible Skipped Contacts */}
                <Collapse in={skippedOpen}>
                  <Box
                    sx={{
                      mt: 1.5,
                      maxHeight: 120,
                      overflowY: "auto",
                      border: "1px solid #fed7aa",
                      borderRadius: "10px",
                      bgcolor: "#fffaf0",
                    }}
                  >
                    {invalidContacts.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 0.7,
                          borderBottom: "1px solid #ffedd5",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: 10,
                            bgcolor: "#e2e8f0",
                            color: "#64748b",
                          }}
                        >
                          {initialsOf(c.name)}
                        </Avatar>
                        <Typography
                          variant="caption"
                          sx={{ color: "#78716c", flex: 1 }}
                          noWrap
                        >
                          {c.name || "Unnamed contact"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#9a3412", fontSize: 11 }}
                        >
                          Missing phone
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>

              {/* Beloved / Honoree Name */}
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Beloved / Honoree Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={customDeceasedName}
                  onChange={(e) => setCustomDeceasedName(e.target.value)}
                  placeholder="e.g. Tafadzwa Makadi"
                  sx={{
                    mt: 0.75,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "14px",
                      bgcolor: "#ffffff",
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.5 }}>
                  Embedded dynamically into the tribute visual and personalized text.
                </Typography>
              </Box>

              {/* Message Format Selector */}
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Message Format
                </Typography>
                <ToggleButtonGroup
                  value={sendFormat}
                  exclusive
                  onChange={(_, val) => val && setSendFormat(val)}
                  fullWidth
                  size="small"
                  sx={{ mt: 0.75 }}
                >
                  <ToggleButton
                    value="image"
                    sx={{
                      py: 1.25,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px!important",
                      border: "1px solid #cbd5e1!important",
                      "&.Mui-selected": {
                        bgcolor: "#ecfdf5!important",
                        borderColor: "#25D366!important",
                        color: "#065f46!important",
                        boxShadow: "0 1px 3px rgba(37, 211, 102, 0.15)",
                      },
                    }}
                  >
                    <ImageIcon sx={{ mr: 1, fontSize: 18 }} />
                    Card + Template
                  </ToggleButton>
                  <ToggleButton
                    value="text"
                    sx={{
                      py: 1.25,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px!important",
                      border: "1px solid #cbd5e1!important",
                      "&.Mui-selected": {
                        bgcolor: "#ecfdf5!important",
                        borderColor: "#25D366!important",
                        color: "#065f46!important",
                        boxShadow: "0 1px 3px rgba(37, 211, 102, 0.15)",
                      },
                    }}
                  >
                    <TextFieldsIcon sx={{ mr: 1, fontSize: 18 }} />
                    Text Only
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* RIGHT PANE: LIVE WHATSAPP DEVICE MOCKUP */}
            <Box
              sx={{
                flex: { xs: "1 1 auto", md: "0 0 48%" },
                bgcolor: "#f8fafc",
                p: { xs: 2, md: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Phone Mockup Container */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 340,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
                  border: "1px solid #cbd5e1",
                  bgcolor: "#efeae2",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Simulated WhatsApp Header */}
                <Box
                  sx={{
                    bgcolor: "#075e54",
                    color: "#ffffff",
                    px: 1.75,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#128c7e",
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    M
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      noWrap
                      sx={{ color: "#ffffff", lineHeight: 1.2 }}
                    >
                      Muzukuru Streaming
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#a7f3d0", fontSize: 10.5 }}
                    >
                      Official Business Account ✓
                    </Typography>
                  </Box>
                </Box>

                {/* WhatsApp Chat Body */}
                <Box
                  sx={{
                    p: 1.75,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    minHeight: 330,
                  }}
                >
                  {/* Date badge */}
                  <Box sx={{ width: "100%", textAlign: "center", mb: 1.5 }}>
                    <Chip
                      label="TODAY"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 600,
                        bgcolor: "rgba(255,255,255,0.85)",
                        color: "#54656f",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      }}
                    />
                  </Box>

                  {/* Outgoing Message Bubble */}
                  <Box
                    sx={{
                      maxWidth: "92%",
                      bgcolor: "#dcf8c6",
                      borderRadius: "12px 0 12px 12px",
                      p: 1.25,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                      position: "relative",
                    }}
                  >
                    {/* Graphic Card Preview (If image format) */}
                    {sendFormat === "image" && (
                      <Box
                        sx={{
                          width: 280,
                          height: 280,
                          borderRadius: "8px",
                          overflow: "hidden",
                          bgcolor: "#ffffff",
                          mb: 1,
                          position: "relative",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 1080,
                            height: 1080,
                            transform: "scale(0.25925)", // 280 / 1080
                            transformOrigin: "top left",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            pointerEvents: "none",
                          }}
                        >
                          <ContactsThankYouMessage
                            deceasedName={effectiveDeceasedName}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Message Text */}
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        color: "#111b21",
                        fontSize: 12.5,
                        lineHeight: 1.45,
                      }}
                    >
                      {previewMessage}
                    </Typography>

                    {/* Timestamp & Read Tick */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 10, color: "#667781" }}
                      >
                        12:45 PM
                      </Typography>
                      <DoneAllIcon sx={{ fontSize: 15, color: "#53bdeb" }} />
                    </Stack>
                  </Box>
                </Box>
              </Box>

              {/* Recipient Pager */}
              {validContacts.length > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    maxWidth: 340,
                    mt: 2,
                    px: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handlePrevContact}
                    disabled={validContacts.length <= 1}
                    sx={{ color: "#64748b" }}
                  >
                    <NavigateBeforeIcon fontSize="small" />
                  </IconButton>

                  <Box textAlign="center">
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      display="block"
                      sx={{ color: "#334155" }}
                    >
                      Previewing: {previewContact?.name || "Friend"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontSize: 11 }}
                    >
                      {previewIndex + 1} of {validContacts.length} recipients
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={handleNextContact}
                    disabled={validContacts.length <= 1}
                    sx={{ color: "#64748b" }}
                  >
                    <NavigateNextIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Box>
          </Box>
        )}

        {/* STAGE 2: GENERATING CARD */}
        {modalStage === "generating" && (
          <Box
            sx={{
              py: 10,
              px: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#ecfdf5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                boxShadow: "0 8px 24px rgba(37, 211, 102, 0.2)",
              }}
            >
              <CircularProgress
                size={40}
                thickness={4}
                sx={{ color: "#25D366" }}
              />
            </Box>

            <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", mb: 0.5 }}>
              {statusStep || "Processing messages..."}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 420, mb: 3 }}>
              {statusSubtitle || "Preparing your messages for delivery..."}
            </Typography>

            <Box sx={{ width: "100%", maxWidth: 360 }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": { bgcolor: "#25D366" },
                }}
              />
            </Box>
          </Box>
        )}

        {/* STAGE 3: LIVE MONITORING & RESULTS REPORT */}
        {modalStage === "monitoring" && (
          <Box sx={{ p: 3 }}>
            {/* Header / Summary Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                mb: 2.5,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                gap={1.5}
                mb={2}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0f172a" }}>
                    {isCompleted ? "Delivery Finished" : "Delivering in Background..."}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {isCompleted
                      ? `All ${batchProgress?.total ?? validContacts.length} contacts processed.`
                      : `Processed ${batchProgress?.processed ?? 0} of ${batchProgress?.total ?? validContacts.length} (${progressPercent}%)`}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a!important" }} />}
                    label={`${batchProgress?.sent ?? 0} Delivered`}
                    size="small"
                    sx={{
                      bgcolor: "#ecfdf5",
                      color: "#065f46",
                      fontWeight: 700,
                      border: "1px solid #a7f3d0",
                    }}
                  />
                  {(batchProgress?.failed ?? 0) > 0 && (
                    <Chip
                      icon={<ErrorIcon sx={{ fontSize: 16, color: "#dc2626!important" }} />}
                      label={`${batchProgress?.failed} Failed`}
                      size="small"
                      sx={{
                        bgcolor: "#fef2f2",
                        color: "#991b1b",
                        fontWeight: 700,
                        border: "1px solid #fecaca",
                      }}
                    />
                  )}
                  {!isCompleted && (
                    <Chip
                      icon={<HourglassEmptyIcon sx={{ fontSize: 15, color: "#64748b!important" }} />}
                      label={`${(batchProgress?.total ?? validContacts.length) - (batchProgress?.processed ?? 0)} In Queue`}
                      size="small"
                      sx={{
                        bgcolor: "#f1f5f9",
                        color: "#475569",
                        fontWeight: 600,
                        border: "1px solid #e2e8f0",
                      }}
                    />
                  )}
                </Stack>
              </Stack>

              {/* Progress Bar */}
              <Box sx={{ width: "100%", position: "relative" }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e2e8f0",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: isCompleted ? "#16a34a" : "#25D366",
                      transition: "transform 0.4s ease",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Filter Tabs & Quick Actions */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1.5}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant={filterTab === "all" ? "contained" : "text"}
                  onClick={() => setFilterTab("all")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: 12,
                    py: 0.5,
                    px: 1.5,
                    bgcolor: filterTab === "all" ? "#0f172a" : "transparent",
                    color: filterTab === "all" ? "#ffffff" : "#64748b",
                  }}
                >
                  All ({mergedRecipients.length})
                </Button>
                <Button
                  size="small"
                  variant={filterTab === "sent" ? "contained" : "text"}
                  onClick={() => setFilterTab("sent")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: 12,
                    py: 0.5,
                    px: 1.5,
                    bgcolor: filterTab === "sent" ? "#16a34a" : "transparent",
                    color: filterTab === "sent" ? "#ffffff" : "#16a34a",
                  }}
                >
                  Delivered ({mergedRecipients.filter((r) => r.status === "sent").length})
                </Button>
                {failedRecipients.length > 0 && (
                  <Button
                    size="small"
                    variant={filterTab === "failed" ? "contained" : "text"}
                    onClick={() => setFilterTab("failed")}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: 12,
                      py: 0.5,
                      px: 1.5,
                      bgcolor: filterTab === "failed" ? "#dc2626" : "transparent",
                      color: filterTab === "failed" ? "#ffffff" : "#dc2626",
                    }}
                  >
                    Failed ({failedRecipients.length})
                  </Button>
                )}
              </Stack>

              {failedRecipients.length > 0 && (
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={handleCopyFailedNumbers}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#dc2626",
                    "&:hover": { bgcolor: "#fef2f2" },
                  }}
                >
                  Copy Failed Numbers
                </Button>
              )}
            </Stack>

            {/* Recipients Live List */}
            <Box
              sx={{
                maxHeight: 280,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                bgcolor: "#ffffff",
              }}
            >
              {filteredRecipients.map((c, idx) => (
                <Box
                  key={c.id || idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    px: 2,
                    py: 1.2,
                    borderBottom:
                      idx < filteredRecipients.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                    bgcolor:
                      c.status === "failed"
                        ? "rgba(254, 242, 242, 0.4)"
                        : "transparent",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0} flex={1}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor:
                          c.status === "sent"
                            ? "#16a34a"
                            : c.status === "failed"
                              ? "#dc2626"
                              : avatarColorFor(c.name),
                        color: "#ffffff",
                      }}
                    >
                      {initialsOf(c.name)}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ color: "#1e293b" }}
                      >
                        {c.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", fontSize: 11.5 }}
                      >
                        {c.phone}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status Badge */}
                  <Box textAlign="right">
                    {c.status === "sent" && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a!important" }} />}
                        label="Delivered"
                        size="small"
                        sx={{
                          bgcolor: "#ecfdf5",
                          color: "#065f46",
                          fontWeight: 600,
                          fontSize: 11,
                          border: "1px solid #a7f3d0",
                        }}
                      />
                    )}
                    {c.status === "failed" && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title={c.error || "Message delivery failed"}>
                          <Chip
                            icon={<ErrorIcon sx={{ fontSize: 14, color: "#dc2626!important" }} />}
                            label={c.error ? c.error.slice(0, 24) + "..." : "Failed"}
                            size="small"
                            sx={{
                              bgcolor: "#fef2f2",
                              color: "#991b1b",
                              fontWeight: 600,
                              fontSize: 11,
                              border: "1px solid #fecaca",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </Stack>
                    )}
                    {c.status === "pending" && (
                      <Chip
                        icon={<CircularProgress size={12} sx={{ color: "#64748b" }} />}
                        label="In Queue"
                        size="small"
                        sx={{
                          bgcolor: "#f1f5f9",
                          color: "#64748b",
                          fontWeight: 600,
                          fontSize: 11,
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}

              {filteredRecipients.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    No contacts match this filter.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* STAGE 4: ERROR */}
        {modalStage === "error" && (
          <Box
            sx={{
              py: 8,
              px: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#fff1f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <ErrorIcon sx={{ color: "#e11d48", fontSize: 38 }} />
            </Box>

            <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", mb: 1 }}>
              Failed to Dispatch Messages
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 420, mb: 3 }}>
              An error occurred while connecting to the Meta Business API. Please check your credentials or try again.
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={handleFinish}
                sx={{ borderRadius: "10px", textTransform: "none" }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => setModalStage("compose")}
                sx={{
                  borderRadius: "10px",
                  bgcolor: "#0f172a",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#1e293b" },
                }}
              >
                Back to Compose
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* FOOTER ACTIONS */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#ffffff",
          borderTop: "1px solid #f1f5f9",
          justifyContent: "space-between",
        }}
      >
        {modalStage === "compose" && (
          <>
            <Button
              onClick={handleFinish}
              sx={{
                color: "#64748b",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 2,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSendBulk}
              disabled={validContacts.length === 0}
              startIcon={<SendIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor: "#25D366",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: "10px",
                px: 3,
                py: 1,
                boxShadow: "0 4px 14px rgba(37, 211, 102, 0.3)",
                "&:hover": {
                  bgcolor: "#1ebd5a",
                  boxShadow: "0 6px 18px rgba(37, 211, 102, 0.4)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#cbd5e1",
                  color: "#ffffff",
                  boxShadow: "none",
                },
              }}
            >
              Send {validContacts.length} WhatsApp Message{validContacts.length === 1 ? "" : "s"}
            </Button>
          </>
        )}

        {modalStage === "monitoring" && (
          <>
            <Button
              onClick={handleFinish}
              sx={{
                color: "#64748b",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 2,
              }}
            >
              {isCompleted ? "Close" : "Run in Background"}
            </Button>

            <Button
              variant="contained"
              onClick={handleFinish}
              sx={{
                bgcolor: isCompleted ? "#16a34a" : "#0f172a",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: "10px",
                px: 3.5,
                py: 1,
                "&:hover": {
                  bgcolor: isCompleted ? "#15803d" : "#1e293b",
                },
              }}
            >
              {isCompleted ? "Done & Return" : "Keep Monitoring"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
